import React, { useState, useEffect } from 'react';
import { Typography, Card, Spin, Tag, Alert } from 'antd';
import {
  UserOutlined,
  KeyOutlined,
  RightOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
  gold: '#D4AF37',
  goldDim: 'rgba(212, 175, 55, 0.15)',
  goldHover: 'rgba(212, 175, 55, 0.25)',
};

const { Title, Paragraph } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  
  h2 {
    margin-bottom: 8px !important;
    font-size: 24px !important;
    font-weight: 600 !important;
    color: ${COLORS.textPrimary} !important;
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${COLORS.accent} !important;
    transform: translateY(-2px);
  }

  .ant-card-body {
    padding: 20px !important;
  }
`;

const SettingItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .setting-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;

    .setting-icon {
      font-size: 24px;
      color: ${COLORS.accent};
    }

    .setting-details {
      .setting-title {
        font-size: 16px;
        font-weight: 500;
        color: ${COLORS.textPrimary};
        margin-bottom: 4px;
      }

      .setting-description {
        font-size: 13px;
        color: ${COLORS.textSecondary};
        margin: 0;
      }
    }
  }

  .setting-arrow {
    color: ${COLORS.textSecondary};
    font-size: 16px;
  }
`;

const ValidationCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;

  .ant-card-body {
    padding: 12px !important;
  }

  .ant-card-head {
    padding: 8px 12px !important;
    min-height: auto !important;
  }

  .ant-card-head-title {
    padding: 0 !important;
    font-size: 14px !important;
  }
`;

const ValidationItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid ${COLORS.border};

  &:last-child {
    border-bottom: none;
  }

  .item-info {
    flex: 1;
    min-width: 0;

    .item-label {
      font-weight: 500;
      color: ${COLORS.textPrimary};
      font-size: 13px;
      display: flex;
      align-items: center;
      gap: 4px;
      margin-bottom: 1px;
      line-height: 1.2;
    }

    .item-description {
      color: ${COLORS.textSecondary};
      font-size: 10px;
      margin-bottom: 0;
      line-height: 1.2;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .item-link {
    color: ${COLORS.accent};
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    transition: all 0.2s;
    white-space: nowrap;
    margin-left: 8px;
    flex-shrink: 0;

    &:hover {
      color: ${COLORS.accent};
      opacity: 0.8;
    }
  }
`;

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [validationData, setValidationData] = useState<any>(null);
  const [validationLoading, setValidationLoading] = useState(false);

  useEffect(() => {
    fetchValidation();
  }, []);

  const fetchValidation = async () => {
    try {
      setValidationLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_VALIDATE_INTEGRATION);

      if (response.data?.status === 'success' && response.data?.data) {
        setValidationData(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching validation:', error);
    } finally {
      setValidationLoading(false);
    }
  };

  const settingsItems = [
    {
      key: 'profile',
      icon: <UserOutlined className="setting-icon" />,
      title: 'Profile',
      description: 'Manage your personal account information and preferences',
      link: '/igwena/profile',
    },
    {
      key: 'api-keys',
      icon: <KeyOutlined className="setting-icon" />,
      title: 'API Keys',
      description: 'Manage your RSA public key and API authentication settings',
      link: '/igwena/api-keys',
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2}>Settings</Title>
        <Paragraph style={{ color: COLORS.textSecondary }}>
          Manage your merchant account settings and preferences.
        </Paragraph>
      </HeaderContainer>

      {/* Configuration Status Section */}
      {validationLoading ? (
        <ValidationCard>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        </ValidationCard>
      ) : validationData?.items ? (
        <ValidationCard
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <SettingOutlined style={{ color: COLORS.accent }} />
              <span style={{ fontSize: '14px' }}>Configuration Status</span>
              {validationData.allComplete && (
                <Tag color="success" icon={<CheckCircleOutlined />} style={{ margin: 0, fontSize: '10px', padding: '1px 6px', lineHeight: '18px' }}>
                  All Complete
                </Tag>
              )}
            </div>
          }
        >
          <Alert
            message="Merchant Configuration"
            description="Review and manage your merchant account configuration settings."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          {validationData.items.map((item: any) => (
            <ValidationItem key={item.key}>
              <div className="item-info">
                <div className="item-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {item.label}
                </div>
                <div className="item-description">
                  {item.description}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                {item.isComplete ? (
                  <Tag color="success" icon={<CheckCircleOutlined />} style={{ margin: 0, fontSize: '10px', padding: '1px 6px', lineHeight: '18px' }}>
                    Complete
                  </Tag>
                ) : (
                  <Tag color="error" icon={<CloseCircleOutlined />} style={{ margin: 0, fontSize: '10px', padding: '1px 6px', lineHeight: '18px' }}>
                    Incomplete
                  </Tag>
                )}
                <span
                  className="item-link"
                  onClick={() => navigate(item.link)}
                >
                  Configure <RightOutlined style={{ fontSize: '9px' }} />
                </span>
              </div>
            </ValidationItem>
          ))}
        </ValidationCard>
      ) : null}

      {/* Existing Settings Items */}
      {settingsItems.map((item) => (
        <StyledCard
          key={item.key}
          onClick={() => navigate(item.link)}
          hoverable
        >
          <SettingItem>
            <div className="setting-info">
              {item.icon}
              <div className="setting-details">
                <div className="setting-title">{item.title}</div>
                <Paragraph className="setting-description">
                  {item.description}
                </Paragraph>
              </div>
            </div>
            <RightOutlined className="setting-arrow" />
          </SettingItem>
        </StyledCard>
      ))}
    </PageContainer>
  );
};

export default Settings;

